// Heavybit Identity token verification and the claim contract.
//
// CONVERGENCE MODULE. This file is copied verbatim into heavybit/crucible
// (src/auth/stytch-verify.ts) together with fixtures/stytch-verify.fixtures.json,
// and both repos run the same vectors. Change it here, run the fixture
// generator, copy both files, and bump CLAIM_CONTRACT_VERSION when a claim is
// added, removed, or renamed. No shared npm package by decision (Prompt 0,
// section 9).
//
// What it verifies: a Stytch session JWT or Connected App access token by
// JWKS (RS256), with an explicit issuer, an explicit audience, expiry with a
// small clock tolerance, and, for MCP servers, a required scope. Every failure
// is a value, never a throw, so an anonymous-first server can map any
// rejection to its anonymous principal and a 401-first server to its door.
//
// Token shape, verified in Stytch Test on 2026-09-10 with a real login and a
// third_party_public client, and confirmed against stytch-node's own
// introspectTokenLocal: session JWTs AND access tokens carry
// aud = ["<project_id>"]; `iss` depends on the HOST the client used to reach
// Stytch: "stytch.com/<project_id>" (no scheme; documented by Stytch as
// non-OIDC-compliant) via api/test.stytch.com, or the project's https domain
// (the default <noun>-<verb>-<n>.customers.stytch.dev|com domain, or the custom
// domain id-test/id.heavybit.com) when reached through it. Verifiers accept
// both (stytchIssuers below) and reject anything else. The client id is a
// separate top-level `client_id` claim, and the only place the exact client id
// appears. The access-token template cannot carry it: `{{ request.client_id }}`
// renders null there and Hasura rejects the whole x-hasura-* block on a single
// null claim. The template stamps x-hasura-agent-id as a literal label instead
// ("mcp-dcr"; the retired CLI client used "auger-cli"), which is all Postgres
// provenance needs.
// RFC 8707 `resource` is accepted by the
// authorize endpoint and ignored: aud never changes. So the per-server scope
// (auger:read, crucible:read) is the ONLY thing that stops a token minted for
// one server being replayed at the other. Never treat aud as a per-server
// boundary.
//
// What it does NOT do: revocation checks (JWTs stay valid locally up to their
// expiry; revoke lag is bounded by the token lifetime) and role permission
// decisions (Hasura owns those).
import {
  createLocalJWKSet,
  createRemoteJWKSet,
  type JSONWebKeySet,
  type JWTPayload,
  jwtVerify,
  type JWTVerifyGetKey,
} from "jose"

export const CLAIM_CONTRACT_VERSION = 5
export const HASURA_CLAIMS = "https://hasura.io/jwt/claims"

export type UserClass = "staff" | "network"

// One principal shape for the SPA session, the Auger MCP server, the CLI,
// and Crucible. userId is the Stytch member id (x-hasura-user-id, contract 5):
// it names one membership, so it differs per organization and is never a
// person key. The person key is personId (the person record id, the same on
// every membership one person holds and on staging and production). Older
// tokens carry a users.id here; it is only ever read as an opaque string.
//
// personId and orgId are numbers, converted once here so no caller tests the
// raw claim: the string "0" is truthy and the number 0 is not. orgId 0 is the
// Network organization, which has no organization record; no record has id 0,
// so an org-scoped rule matches nothing and fails closed. clientId is the
// top-level client_id claim exactly as Stytch put it there: present on a
// Connected App access token, absent on a session JWT.
export interface Principal {
  userId: string
  class: UserClass
  defaultRole: string
  roles: string[]
  email?: string
  agentId?: string
  clientId?: string
  personId?: number
  orgId?: number
  scopes: string[]
  sub: string
}

export type VerifyFailure =
  | "malformed"
  | "expired"
  | "invalid_signature"
  | "invalid_issuer"
  | "invalid_audience"
  | "missing_scope"
  | "invalid_claims"
  // The key set could not be fetched, so nothing is known about this token.
  // Servers must answer 503 and NOT a 401 challenge: a 401 tells the client
  // its token is bad, and a client that believes that throws away a perfectly
  // good token and re-authorizes. A Stytch JWKS blip would otherwise log out
  // every connected client at once.
  | "key_unavailable"

export type VerifyResult =
  | { ok: true; principal: Principal; payload: JWTPayload }
  | { ok: false; reason: VerifyFailure }

export interface StytchVerifierOptions {
  /** Accepted `iss` values; use stytchIssuers(projectId, domain) to build the same allow-list stytch-node accepts. */
  issuer: string | string[]
  /** A JWKS document, or the URL of one (remote sets are cached and refreshed on unknown kid). */
  jwks: JSONWebKeySet | URL | string
  /** Accepted `aud` values: the project id, for BOTH session JWTs and access tokens. Never the client id. */
  audience?: string | string[]
  /** MCP servers set their own scope (auger:read, crucible:read); tokens without it are rejected. This is the only replay boundary. */
  requiredScope?: string
  clockToleranceSeconds?: number
}

export interface StytchVerifier {
  verify(token: string): Promise<VerifyResult>
}

/**
 * The issuer allow-list for a project, the way stytch-node builds it: the
 * bare `stytch.com/<project_id>` plus the https project or custom domain when
 * one is configured. Trailing slashes are dropped; a domain without a scheme
 * is refused because `authorization_servers` and OIDC both need an https URL.
 */
export function stytchIssuers(projectId: string, domain?: string): string[] {
  if (!/^project-(test|live)-[a-z0-9-]+$/.test(projectId)) {
    throw new Error(`not a Stytch project id: ${projectId}`)
  }
  const origin = parseStytchDomain(domain)
  return origin ? [`stytch.com/${projectId}`, origin] : [`stytch.com/${projectId}`]
}

/**
 * Normalize a configured custom auth domain: trailing slashes dropped, empty
 * read as absent. Anything that is not a bare https origin throws, because
 * every API path is appended to it and a path or a missing scheme silently
 * mis-routes every call.
 */
export function parseStytchDomain(raw: string | undefined): string | undefined {
  const trimmed = raw?.trim().replace(/\/+$/, "")
  if (!trimmed) return undefined
  if (!/^https:\/\/[^/\s]+$/.test(trimmed)) {
    throw new Error(`Stytch domain must be an https origin with no path: ${raw}`)
  }
  return trimmed
}

/**
 * The API base for server-side calls. Stytch derives the token issuer from the
 * host that was called, so with a custom domain configured every caller must
 * use it. Without one, test projects use test.stytch.com and live projects
 * api.stytch.com.
 */
export function stytchApiBase(projectId: string, domain?: string): string {
  const origin = parseStytchDomain(domain)
  if (origin) return origin
  return projectId.startsWith("project-test-") ? "https://test.stytch.com" : "https://api.stytch.com"
}

/**
 * Where the project's signing keys live. Through the custom domain, the OIDC
 * discovery document points at <domain>/.well-known/jwks.json (the same kid as
 * the stytch.com host); without one, the B2B session JWKS path on the default
 * host.
 */
export function stytchJwksUrl(projectId: string, domain?: string): string {
  const origin = parseStytchDomain(domain)
  if (origin) return `${origin}/.well-known/jwks.json`
  return `${stytchApiBase(projectId)}/v1/b2b/sessions/jwks/${projectId}`
}

// Shape of __fixtures__/stytch-verify.fixtures.json (shared with crucible).
export interface StytchVerifyFixtures {
  claim_contract_version: number
  project_id: string
  issuer: string
  custom_domain_issuer: string
  audience: string
  client_id: string
  /** Clock tolerance the vectors were generated for; the skew vectors sit either side of it. */
  clock_tolerance_seconds: number
  /** Unix seconds the vectors carrying `now` must be evaluated at. */
  now: number
  jwks: JSONWebKeySet
  tokens: Record<string, string>
  vectors: Array<{
    name: string
    token: string
    audience?: string
    required_scope?: string
    /** Evaluate this vector with the clock pinned here (unix seconds), not at the real time. */
    now?: number
    expect: { ok: true; principal: Principal } | { ok: false; reason: VerifyFailure }
  }>
}

function keySetFor(jwks: StytchVerifierOptions["jwks"]): JWTVerifyGetKey {
  if (typeof jwks === "string") return createRemoteJWKSet(new URL(jwks))
  if (jwks instanceof URL) return createRemoteJWKSet(jwks)
  return createLocalJWKSet(jwks)
}

function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  return value.every((v) => typeof v === "string") ? (value as string[]) : null
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined
}

function recordId(value: unknown): number | undefined {
  return typeof value === "string" && /^[1-9][0-9]*$/.test(value) ? Number(value) : undefined
}

// Today's network tokens carry "" (Hasura rejects a null x-hasura-* claim);
// the target claim template writes "0". Both mean the Network organization.
function orgIdFrom(value: unknown): number | undefined {
  if (value === "" || value === "0") return 0
  return recordId(value)
}

// The audience roles, the same lists as STAFF_ROLES in permissions.ts and the
// session roles in claim-contract.js. This file imports nothing local, on
// purpose (Crucible copies it), so the lists are repeated here and a test
// holds them equal to permissions.ts.
export const STAFF_AUDIENCE_ROLES = ["heavybit_staff", "heavybit_admin"] as const
export const NETWORK_AUDIENCE_ROLES = ["network_member"] as const

/**
 * The caller's audience. The `x-hasura-user-class` claim comes from
 * users.class, which goes away with the users table, so its absence is not an
 * error: the audience is then read from the audience roles the token carries.
 * Only a role Hasura has permissions for counts. heavybit_agent and
 * portfolio_* name no person's audience, so a token that holds only those is
 * refused. A claim that is present must still be one of the two known values.
 */
function classFrom(claim: unknown, roles: readonly string[]): UserClass | null {
  if (claim === "staff" || claim === "network") return claim
  if (claim !== undefined && claim !== null && claim !== "") return null
  const holds = (list: readonly string[]) => roles.some((role) => list.includes(role))
  if (holds(STAFF_AUDIENCE_ROLES)) return "staff"
  if (holds(NETWORK_AUDIENCE_ROLES)) return "network"
  return null
}

/**
 * Build the Principal from verified claims. Returns null when the token does
 * not carry the contract: no Hasura block, no user id, or a class outside
 * staff|network. Agent provenance is the top-level `client_id` claim Stytch
 * puts on every access token, and it is preferred because it is the exact
 * client id. The x-hasura-agent-id fallback below carries only a coarse label
 * ("mcp-dcr"; the retired CLI client used "auger-cli") on a current token,
 * since a template cannot render the client id — enough for Postgres
 * provenance, not for identifying a client. Any non-empty value counts.
 */
export function principalFromClaims(payload: JWTPayload): Principal | null {
  const block = payload[HASURA_CLAIMS]
  if (!block || typeof block !== "object" || Array.isArray(block)) return null
  const claims = block as Record<string, unknown>

  const userId = nonEmptyString(claims["x-hasura-user-id"])
  const defaultRole = nonEmptyString(claims["x-hasura-default-role"])
  const roles = asStringArray(claims["x-hasura-allowed-roles"])
  const sub = nonEmptyString(payload.sub)
  if (!userId || !defaultRole || !roles || !sub) return null
  const userClass = classFrom(claims["x-hasura-user-class"], roles)
  if (!userClass) return null

  const scope = nonEmptyString(payload.scope)
  const clientId = nonEmptyString(payload.client_id)
  return {
    userId,
    class: userClass,
    defaultRole,
    roles,
    email: nonEmptyString(payload.email),
    agentId: clientId ?? nonEmptyString(claims["x-hasura-agent-id"]),
    clientId,
    personId: recordId(claims["x-hasura-person-id"]),
    orgId: orgIdFrom(claims["x-hasura-org-id"]),
    scopes: scope ? scope.split(" ").filter(Boolean) : [],
    sub,
  }
}

/**
 * "We could not reach the key set", as jose 6 reports it:
 *
 *   - a timeout is JWKSTimeout (ERR_JWKS_TIMEOUT);
 *   - a non-200 (including the 3xx that `redirect: "manual"` surfaces) and an
 *     unparseable body are a bare JOSEError, identifiable only by message;
 *   - an unreachable host is the platform's own fetch rejection, passed
 *     through untouched: a TypeError with no jose `code`.
 *
 * jose caches a good key set for cacheMaxAge and then rethrows past it, so
 * this is not a startup-only condition.
 */
function isKeySetUnavailable(err: unknown): boolean {
  if (!err || typeof err !== "object") return false
  const code = "code" in err ? String((err as { code: unknown }).code) : ""
  const message = "message" in err ? String((err as { message: unknown }).message) : ""
  if (code === "ERR_JWKS_TIMEOUT") return true
  if (code === "ERR_JOSE_GENERIC" && message.includes("JSON Web Key Set HTTP response")) return true
  // No jose code at all: every decode and claim failure carries one, so what
  // is left is the fetch itself failing.
  return !code && (err instanceof TypeError || "cause" in err)
}

function reasonFor(err: unknown): VerifyFailure {
  if (isKeySetUnavailable(err)) return "key_unavailable"
  const code = err && typeof err === "object" && "code" in err ? String((err as { code: unknown }).code) : ""
  const claim = err && typeof err === "object" && "claim" in err ? String((err as { claim: unknown }).claim) : ""
  switch (code) {
    case "ERR_JWT_EXPIRED":
      return "expired"
    case "ERR_JWT_CLAIM_VALIDATION_FAILED":
      if (claim === "iss") return "invalid_issuer"
      if (claim === "aud") return "invalid_audience"
      return "invalid_claims"
    case "ERR_JWS_SIGNATURE_VERIFICATION_FAILED":
    case "ERR_JWKS_NO_MATCHING_KEY":
    case "ERR_JWKS_MULTIPLE_MATCHING_KEYS":
    case "ERR_JOSE_ALG_NOT_ALLOWED":
      return "invalid_signature"
    default:
      return "malformed"
  }
}

export function createStytchVerifier(options: StytchVerifierOptions): StytchVerifier {
  const getKey = keySetFor(options.jwks)
  const clockTolerance = options.clockToleranceSeconds ?? 60

  return {
    async verify(token: string): Promise<VerifyResult> {
      // A dotless opaque token is not a JWT; do not spend a key lookup on it.
      if (typeof token !== "string" || token.split(".").length !== 3) {
        return { ok: false, reason: "malformed" }
      }
      let payload: JWTPayload
      try {
        const verified = await jwtVerify(token, getKey, {
          issuer: options.issuer,
          audience: options.audience,
          algorithms: ["RS256"],
          // jose checks exp only when the token carries it; a token without
          // one would otherwise never expire.
          requiredClaims: ["exp"],
          clockTolerance,
        })
        payload = verified.payload
      } catch (err) {
        return { ok: false, reason: reasonFor(err) }
      }
      const principal = principalFromClaims(payload)
      if (!principal) return { ok: false, reason: "invalid_claims" }
      if (options.requiredScope && !principal.scopes.includes(options.requiredScope)) {
        return { ok: false, reason: "missing_scope" }
      }
      return { ok: true, principal, payload }
    },
  }
}

// ── The MCP 401 door, identical on both servers ────────────────────────────

/**
 * RFC 9728 protected-resource metadata: the AS is the Stytch issuer. A server
 * advertises every scope it can use, because a client requests what is
 * advertised and a scope nobody requests can never be granted.
 */
export function buildProtectedResourceMetadata(input: { resource: string; issuer: string; scopes: string[] }) {
  return {
    resource: input.resource,
    authorization_servers: [input.issuer],
    scopes_supported: input.scopes,
    bearer_methods_supported: ["header"],
  }
}

/** The WWW-Authenticate challenge. Parameter order is fixed so both servers emit byte-identical headers. */
export function buildWwwAuthenticate(input: {
  resourceMetadataUrl: string
  realm?: string
  error?: string
  scopes?: string[]
}): string {
  const params: string[] = []
  if (input.realm) params.push(`realm="${input.realm}"`)
  params.push(`resource_metadata="${input.resourceMetadataUrl}"`)
  if (input.error) params.push(`error="${input.error}"`)
  if (input.scopes?.length) params.push(`scope="${input.scopes.join(" ")}"`)
  return `Bearer ${params.join(", ")}`
}
